<template>
  <v-card class="profile-section-dialog">
    <!-- start of header -->
    <v-toolbar flat class="header">
      <span class="headline">{{ layoutSetting.section.caption }}</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="hideProfileSectionDialog">
        <v-icon>close</v-icon>
      </v-btn>
    </v-toolbar>
    <!-- start of header //:~ -->

    <v-card-text class="content">
      <v-container class="card-text-layout">

        <!-- start of title -->
        <div class="section-banner" v-if="layoutSetting.title && layoutSetting.title instanceof Array">
          <v-layout row>
            <v-flex v-for="(title, index) in layoutSetting.title" :key="index"
                    :class="'lg' + 12 / layoutSetting.title.length">
              <label class="m-r-20" v-if="showTitle(assist.dataType.LoanTypeEnum.INSTALLMENT_LOAN.value)">{{
                  title.label.installmentLoan
                }}</label>
              <label class="m-r-20" v-if="showTitle(assist.dataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value)">{{
                  title.label.multipleAdvanceLoan
                }}</label>
              <strong :class="title.class">
                {{ assist.tool.fieldRender(current.sectionData, title.template, "currency") }}
              </strong>
            </v-flex>
          </v-layout>
        </div>
        <!-- end of title //:~ -->

        <!-- start of table section -->
        <div v-if="layoutSetting.section.type === 'table'">
          <v-data-table
            :headers="layoutSetting.headers"
            :items="current.tableData"
            hide-default-footer
          >
            <template v-slot:item="props">
              <tr>
                <td>{{ props.index + 1 }}</td>
                <td v-for="(field, index) in layoutSetting.formFields" :key="index">
                  <v-select
                    v-if="field.controlType==='v-select'"
                    v-model="props.item[field.fieldKey]"
                    :items="field.options[field.fieldKey]"
                    :label="field.label"
                    single-line
                    hide-details
                    style="min-width: 200px"
                    :disabled="isTableDisabled"
                  ></v-select>
                  <v-text-field
                    v-else-if="field.controlType===undefined"
                    v-model="props.item[field.fieldKey]"
                    prefix="$"
                    type="number"
                    style="max-width: 105px"
                    hide-details
                    :disabled="isTableDisabled"
                    autocomplete="off"
                  ></v-text-field>
                  <date-picker-menu v-else v-model="props.item[field.fieldKey]" :label="field.label"
                                    :disabled="isTableDisabled" :allowed="field.allowed"
                                    :key="index + Math.floor(Math.random()*1000)"></date-picker-menu>
                </td>
                <td>
                  <v-btn depressed outlined icon fab dark color="pink" small
                         v-if="setting.action === 'EDIT'"
                         @click="editItems('DELETE', props.item, props.index)"
                         :disabled="isTableDisabled"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
        <!-- end of table section //:~ -->

        <!-- start of form section -->
        <div v-else>
          <v-layout row wrap>
            <template
              v-if="loan.loanType === assist.dataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value && layoutSetting.section.name === 'payment-option'">
              <template v-for="(field, index) in layoutSetting.multipleLoanFormFields">
                <v-flex
                  :class="field.class === undefined ? 'lg6' : field.class"
                  :key="assist.tool.getRandomKey(index)"
                  v-if="displayByPayrollFrequency(field.fieldKey, field.loanType) && isShowPaymentMode(field.individualTransactionMode)"
                >
                  <ValidationObserver ref="observer" v-slot="{ validate, reset }">
                    <v-layout align-center>
                      <ValidationProvider :name="field.label" :rules="field.rules" v-slot="{ errors }"
                                          style="width: 100%;margin-left: 10px">
                        <v-select
                          v-if="field.controlType === 'v-select'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :items="field.fieldKey === 'program'
                      ? getOptions(field.options)
                      : (field.fieldKey.indexOf('DayOfMonth') !== -1 ? dayOfMonthOptions : field.options[field.fieldKey])"
                          @change="changeSelect(field.fieldKey, current.sectionData[field.fieldKey])"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-select>
                        <v-textarea
                          v-else-if="field.controlType === 'v-textarea'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-textarea>
                        <v-radio-group
                          v-else-if="field.controlType === 'v-radio-group'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          row
                        >
                          <v-radio
                            v-for="(option, index) in field.options[field.fieldKey]"
                            color="primary"
                            :key="index"
                            :label="option.text"
                            :value="option.value"
                            :disabled="setFieldDisableStatus(field)"
                          ></v-radio>
                        </v-radio-group>
                        <v-switch
                          v-else-if="field.controlType === 'v-switch'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          value="1"
                          :disabled="setFieldDisableStatus(field)"
                          class="ma-2"
                          color="primary"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-switch>
                        <div v-else-if="field.controlType === 'v-date-picker'">
                          <date-picker-menu
                            v-model="current.sectionData[field.fieldKey]"
                            :label="field.label"
                            :disabled="setFieldDisableStatus(field)"
                            :allowed="getLimit(field)"
                            :error-messages="errors[0]"
                            hide-details="auto"
                            :holiday="getHoliday(field.fieldKey)"
                          ></date-picker-menu>
                        </div>
                        <v-text-field
                          v-else-if="assist.tool.isNotEmpty(field.mask)"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          v-mask="field.mask"
                          autocomplete="off"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          autocomplete="off"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-layout>
                  </ValidationObserver>
                </v-flex>
              </template>
            </template>
            <template v-else>
              <template v-for="(field, index) in layoutSetting.formFields">
                <v-flex
                  :class="field.class === undefined ? 'lg6' : field.class"
                  :key="assist.tool.getRandomKey(index)"
                  v-if="displayByPayrollFrequency(field.fieldKey, field.loanType) && isShowPaymentMode(field.individualTransactionMode)"
                >
                  <ValidationObserver ref="observer" v-slot="{ validate, reset }">
                    <v-layout align-center>
                      <ValidationProvider :name="field.label" :rules="field.rules" v-slot="{ errors }"
                                          style="width: 100%;margin-left: 10px">
                        <v-select
                          v-if="field.controlType === 'v-select'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :items="field.fieldKey === 'program'
                      ? getOptions(field.options)
                      : (field.fieldKey.indexOf('DayOfMonth') !== -1 ? dayOfMonthOptions : field.options[field.fieldKey])"
                          @change="changeSelect(field.fieldKey, current.sectionData[field.fieldKey])"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-select>
                        <v-textarea
                          v-else-if="field.controlType === 'v-textarea'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-textarea>
                        <v-radio-group
                          v-else-if="field.controlType === 'v-radio-group'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          row
                        >
                          <v-radio
                            v-for="(option, index) in field.options[field.fieldKey]"
                            color="primary"
                            :key="index"
                            :label="option.text"
                            :value="option.value"
                            :disabled="setFieldDisableStatus(field)"
                          ></v-radio>
                        </v-radio-group>
                        <v-switch
                          v-else-if="field.controlType === 'v-switch'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          value="1"
                          :disabled="setFieldDisableStatus(field)"
                          class="ma-2"
                          color="primary"
                          :error-messages="errors[0]"
                          hide-details="auto"
                        ></v-switch>
                        <div v-else-if="field.controlType === 'v-date-picker'">
                          <date-picker-menu
                            v-model="current.sectionData[field.fieldKey]"
                            :label="field.label"
                            :disabled="setFieldDisableStatus(field)"
                            :allowed="getLimit(field)"
                            :holiday="getHoliday(field.fieldKey)"
                          ></date-picker-menu>
                        </div>
                        <v-text-field
                          v-else-if="assist.tool.isNotEmpty(field.mask)"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          v-mask="field.mask"
                          autocomplete="off"
                        ></v-text-field>
                        <v-text-field
                          v-else-if="assist.tool.isNotEmpty(field.inputType) && field.inputType === 'number'"
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          type="number"
                          autocomplete="off"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          v-model="current.sectionData[field.fieldKey]"
                          :label="field.label"
                          :disabled="setFieldDisableStatus(field)"
                          :error-messages="errors[0]"
                          hide-details="auto"
                          autocomplete="off"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-layout>
                  </ValidationObserver>
                </v-flex>
              </template>
            </template>
          </v-layout>
        </div>
        <!-- end of form section //:~ -->

        <!-- start of question -->
        <div ref="questionSection"
             v-if="assist.tool.isNotEmpty(layoutSetting.questionGroup) && setting.action === 'EDIT'">
          <v-divider class="no-bg m-v-20"></v-divider>
          <template v-for="(questionTemplate, index) in dataSource.questionTemplateData">
            <div :key="assist.tool.getRandomKey(index)" :ref="`question${questionTemplate.id}`">
              <v-layout row wrap>
                <v-flex lg12>
                  <h5>
                    <v-icon>keyboard_arrow_right</v-icon>
                    {{ questionTemplate.question }}
                  </h5>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex lg1></v-flex>
                <v-flex :ref="`answer${questionTemplate.id}`" :class="questionTemplate.id === 2 ? 'lg3' : ''">
                  <v-radio-group
                    row
                    v-if="assist.tool.isNotEmpty(current.questionAnswerData)"
                    v-model="current.questionAnswerData[questionTemplate.id].value"
                  >
                    <v-radio
                      v-for="(option, j) in JSON.parse(questionTemplate.options)"
                      color="primary"
                      :label="option.text"
                      :value="option.value"
                      :key="assist.tool.getRandomKey(j)"
                      @change="changeRadio(questionTemplate.id)"
                    ></v-radio>
                  </v-radio-group>
                </v-flex>
              </v-layout>
            </div>
          </template>
        </div>
        <!-- end of question //:~ -->
        <div v-if="isEditProfile()">
          <v-divider class="m-t-30 m-b-0"></v-divider>
          <v-layout row wrap>
            <v-flex :class="`${setting.fieldClass} p-l-10 p-r-10`">
              <v-form data-vv-scope="form">
                <v-select
                  v-model="dialog.authorizationType"
                  label="Authorization Type"
                  :items="typeItems"
                  item-text="text"
                  item-value="value"
                  v-validate="'required'"
                  data-vv-name="notEmpty"
                  :error-messages="errors.first('form.notEmpty')"
                  hide-details
                ></v-select>
              </v-form>
            </v-flex>
            <v-flex></v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex :class="`${setting.fieldClass} p-l-10 p-r-10`">
              <v-textarea
                rows="2"
                label="Note"
                v-model="dialog.note"
                hide-details
              ></v-textarea>
            </v-flex>
            <v-flex></v-flex>
          </v-layout>
        </div>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-btn
        v-if="setting.action === 'EDIT' && layoutSetting.section.type === 'table'"
        color="primary"
        text
        @click="editItems('ADD')"
        :disabled="isTableDisabled"
      >Add
      </v-btn>

      <template
        v-if="setting.action === 'EDIT' && assist.tool.isNotEmpty(loan) && loan.phase === assist.dataType.LoanPhaseEnum.LOAN_ORIGINATION.value && stamp.display">
        <v-tooltip v-for="(item, index) in stamp.verifications" bottom :key="index">
          <template v-slot:activator="{ on }">
            <v-btn icon dark
                   :color="(assist.tool.isNotEmpty(sectionStampData.lightIconType) && sectionStampData.lightIconType === item.actionType) ? item.color : 'grey'"
                   v-on="on" @click="showStampVerification(item.sectionKey, item.actionType)"
                   :class="item.class">
              <v-icon>{{ item.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ item.text }}</span>
        </v-tooltip>
      </template>
      <v-spacer></v-spacer>
      <v-btn
        text
        @click.native="saveAction()"
        :loading="dialog.loading"
      >Save
      </v-btn>
    </v-card-actions>

    <!-- Stamp Verification Dialog -->
    <v-dialog v-model="dialog.stampVerification" persistent max-width="500">
      <stamp-verification-dialog
        :loan="loan"
        :sectionKey="sectionStampData.sectionKey"
        :stampActionType="sectionStampData.stampActionType"
        :display="dialog.stampVerification"
        @changeLightIconType="changeLightIconType"
        @hideDialog="hideStampReasonHandler"
      ></stamp-verification-dialog>
    </v-dialog>
  </v-card>
</template>

<script>
import { LOAN_AGREEMENT_FACTOR } from '@/setting/algorithm-factor-setting'
import { OriginationBusinessApi, OriginationApi, AlgorithmApi, InstantApi, ProcessingApi } from '@/api/pandaria'
import { DataType, Tool, Crypto, Popup } from '@/util/index'
import DatePickerMenu from '@/components/widget/input/DatePickerMenu'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { SystemEnum } from '@/assets/js'
import StampVerificationDialog from './StampVerificationDialog'

const TOOL = new Tool()
const popup = new Popup()
export default {
  name: 'old-profile-section-dialog',
  components: {
    StampVerificationDialog,
    DatePickerMenu,
    ValidationProvider,
    ValidationObserver
  },
  props: {
    loan: Object,
    slaveLoanId: Number,
    layoutSetting: Object,
    dataSource: Object,
    extension: Object,
    setting: Object,
    typeNote: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      original: {
        sectionData: {},
        tableData: [],
        answerData: [],
        questionAnswerData: {}
      },
      current: {
        sectionData: {},
        tableData: [],
        answerData: [],
        questionAnswerData: {}
      },
      assist: {
        tool: TOOL,
        dataType: DataType
      },
      dialog: {
        formDatePicker: false,
        tableDatePicker: [false],
        loading: false,
        authorizationType: null,
        note: '',
        stampVerification: false
      },
      dayOfMonthOptions: [],
      isTableDisabled: false,
      holiday: [],
      flag: false,
      typeItems: SystemEnum.AUTHORIZATION_TYPE,
      sectionStampData: {
        stampActionType: null,
        sectionKey: null,
        sectionStampRecord: [],
        lightIconType: null
      },
      style: {
        appendix: {
          top: 70,
          left: 300,
          width: 500
        }
      },
      stamp: {
        display: false,
        verifications: []
      },
      blackList: {
        ssn: false,
        email: false,
        state: false,
        accountNo: false,
        routingNo: false,
        zip: false,
        birthday: false
      },
      warningList: {
        zip: false,
        email: false,
        lastPayday: false
      }
    }
  },
  computed: {
    display () {
      return this.setting.display
    },
    loanId () {
      return this.loan.id
    }
  },
  watch: {
    display: {
      handler: function (newVal) {
        if (newVal) {
          this.original = this.$options.data().original

          this.renderQuestionData()
          this.renderSectionData()
        } else {
          this.current.sectionData = {}
        }
      },
      immediate: true
    }
  },
  destroyed () {
    this.$eventBus.$off('updateStamp')
    this.$eventBus.$off('reopenStamp')
  },
  mounted () {
    if (this.layoutSetting.section.name === 'payment-option') {
      const roleCode = this.store.getters.getRoleCode
      const roleEnum = [
        DataType.RoleEnum.RETURNING_AGENT.code,
        DataType.RoleEnum.RETURNING_CUSTOMER_POC.code
      ]
      if (roleEnum.includes(roleCode)) {
        for (const formField of this.layoutSetting.formFields) {
          if (formField.fieldKey === 'program') {
            formField.isEditable = false
          }
        }
      }
      this.holiday = this.store.getters.getHoliday
      this.$eventBus.$on('reopenStamp', target => {
        if (target.loanId === this.loanId) {
          this.stamp.display = true
          this.stamp.verifications = this.layoutSetting.stampVerifications
        }
      })
    }
    this.checkLatestSectionStamp()
    this.$eventBus.$on('updateStamp', target => {
      const sectionName = this.layoutSetting.section.name
      if (TOOL.getSectionKeyByFeature(sectionName) === target) {
        this.checkLatestSectionStamp()
      }
    })
  },
  methods: {
    changeLightIconType (stampType) {
      this.sectionStampData.lightIconType = stampType
    },
    checkLatestSectionStamp () {
      const $this = this
      if (this.layoutSetting.section.name === 'statement') {
        return
      }
      if (TOOL.isNotEmpty(this.loan.id)) {
        const payload = {
          loanId: this.loan.id,
          feature: 'STAMP',
          condition: {
            sectionKey: this.layoutSetting.stampVerifications[0].sectionKey,
            isReopenStamp: true
          }
        }
        OriginationApi.listSectionFeature(payload, function (callbackData) {
          if ($this.assist.tool.isNotEmpty(callbackData)) {
            $this.sectionStampData.sectionStampRecord = callbackData
            $this.sectionStampData.lightIconType = $this.sectionStampData.sectionStampRecord[0].type
            $this.checkStampDisplay()
          } else {
            $this.stamp.display = true
            $this.stamp.verifications = $this.layoutSetting.stampVerifications
          }
        })
      }
    },
    isShowPaymentMode (individualTransactionMode) {
      let portfolioTransactionMode = this.store.getters.getPortfolioJson.individualTransactionMode
      portfolioTransactionMode = TOOL.isNotEmpty(portfolioTransactionMode) ? portfolioTransactionMode : '0'
      if (TOOL.isNotEmpty(individualTransactionMode) && individualTransactionMode !== portfolioTransactionMode) {
        return false
      } else {
        return true
      }
    },
    checkStampDisplay () {
      const loanOperation = this.$store.getters.getLoanOperation
      const request = {
        loanId: this.loanId
      }
      const $this = this
      if (TOOL.isEmpty(loanOperation) || TOOL.isEmpty(loanOperation.auditedId)) {
        this.stamp.display = true
        this.stamp.verifications = this.layoutSetting.stampVerifications
      } else {
        const tags = $this.loan.tags
        if (TOOL.isNotEmpty(tags) &&
          ((DataType.LoanTagsEnum.WAKE_UP.value & tags) === DataType.LoanTagsEnum.WAKE_UP.value)) {
          this.stamp.display = true
          this.stamp.verifications = this.layoutSetting.stampVerifications
        } else {
          for (const item of this.sectionStampData.sectionStampRecord) {
            if (item.type === DataType.StampActionTypeEnum.DECLINE_STAMP.value ||
              item.type === DataType.StampActionTypeEnum.REOPEN_STAMP.value) {
              this.stamp.display = true
              this.stamp.verifications = this.layoutSetting.stampVerifications
              break
            }
          }
        }
      }
      OriginationBusinessApi.judgeEnableStamp(request, function (result) {
        if (result) {
          $this.stamp.display = true
          $this.stamp.verifications = $this.layoutSetting.stampVerifications
        }
      })
    },
    hideStampReasonHandler () {
      this.dialog.stampVerification = false
    },
    showStampVerification (sectionKey, actionType) {
      this.sectionStampData.sectionKey = sectionKey
      this.sectionStampData.stampActionType = actionType
      this.dialog.stampVerification = true
    },
    showTitle (loanType) {
      return this.loan.loanType === loanType
    },
    isEditProfile () {
      return this.typeNote && this.loan.phase !== DataType.LoanPhaseEnum.LOAN_ORIGINATION.value
    },
    getOptions (options) {
      if (typeof (options) === 'string') {
        // eslint-disable-next-line no-eval
        return eval(options)
      } else {
        return options
      }
    },
    getLimit (field) {
      const portfolio = this.store.getters.getPortfolioJson
      if (field.fieldKey === 'effectiveDate' &&
        this.loan.phase === DataType.LoanPhaseEnum.LOAN_ORIGINATION.value) {
        if ((TOOL.isEmpty(portfolio.individualTransactionMode) || portfolio.individualTransactionMode === '0') &&
          this.current.sectionData.paymentMode === DataType.TransactionModeEnum.INSTANT_FUND.value) {
          return 'start-now,end-now'
        } else if ((TOOL.isNotEmpty(portfolio.individualTransactionMode) && portfolio.individualTransactionMode === '1') &&
          this.current.sectionData.defaultDisbursementMode === DataType.TransactionModeEnum.INSTANT_FUND.value) {
          return 'start-now,end-now'
        } else {
          return field.allowed
        }
      } else {
        return field.allowed
      }
    },
    getHoliday (filedKey) {
      if (filedKey === 'effectiveDate') {
        const portfolio = this.store.getters.getPortfolioJson
        if ((TOOL.isEmpty(portfolio.individualTransactionMode) || portfolio.individualTransactionMode === '0') &&
          this.current.sectionData.paymentMode === DataType.TransactionModeEnum.ACH.value) {
          return this.holiday
        } else if ((TOOL.isNotEmpty(portfolio.individualTransactionMode) && portfolio.individualTransactionMode === '1') &&
          this.current.sectionData.defaultDisbursementMode === DataType.TransactionModeEnum.ACH.value) {
          return this.holiday
        } else {
          return []
        }
      } else {
        return this.holiday
      }
    },
    displayByPayrollFrequency (fieldKey, loanType) {
      if (this.assist.tool.isNotEmpty(loanType) && loanType.indexOf(this.loan.loanType) === -1) {
        return false
      }

      if (fieldKey !== 'firstDayOfWeek' && fieldKey !== 'firstDayOfMonth' && fieldKey !== 'secondDayOfMonth') {
        return true
      }

      const payrollFrequency = this.current.sectionData.payrollFrequency
      return TOOL.displayByPayrollFrequency(fieldKey, payrollFrequency)
    },
    changeSelect (fieldKey, fieldValue) {
      if (fieldKey === 'payrollFrequency') {
        if (fieldValue === DataType.PayrollFrequencyEnum.SEMI_MONTHLY.value) {
          this.dayOfMonthOptions = DataType.DayOfSemiMonth
        }
        if (fieldValue === DataType.PayrollFrequencyEnum.MONTHLY.value) {
          this.dayOfMonthOptions = DataType.DayOfMonth
        }
        this.$forceUpdate()
      }
      if (fieldKey === 'paymentMode' || fieldKey === 'defaultDisbursementMode') {
        if (this.holiday.includes(this.current.sectionData.effectiveDate) && fieldValue === DataType.TransactionModeEnum.ACH.value) {
          this.current.sectionData.effectiveDate = TOOL.getNextStrData(this.current.sectionData.effectiveDate)
        }
        this.$forceUpdate()
      }
    },
    renderQuestionData () {
      if (TOOL.isNotEmpty(this.layoutSetting.questionGroup)) {
        this.current.answerData = []
        this.current.questionAnswerData = {}

        // Object.assign(this.original.answerData, this.dataSource.answerData)
        // Object.assign(this.original.questionAnswerData, this.extension.questionAnswerData)
        this.original.answerData = JSON.parse(JSON.stringify(this.dataSource.answerData))
        this.original.questionAnswerData = JSON.parse(JSON.stringify(this.extension.questionAnswerData))

        // Object.assign(this.current.answerData, this.original.answerData)
        // Object.assign(this.current.questionAnswerData, this.original.questionAnswerData)
        this.current.answerData = JSON.parse(JSON.stringify(this.dataSource.answerData))
        this.current.questionAnswerData = JSON.parse(JSON.stringify(this.extension.questionAnswerData))

        for (const sourceQuestionTemplate of this.dataSource.questionTemplateData) {
          let hasData = false
          for (const sourceAnswer of this.dataSource.answerData) {
            if (sourceQuestionTemplate.id === sourceAnswer.question) {
              hasData = true
            }
          }
          if (!hasData) {
            const currentQuestionAnswer = {
              text: '',
              value: false,
              appendix: ''
            }
            this.current.questionAnswerData[sourceQuestionTemplate.id] = currentQuestionAnswer
          }
        }
      }
    },
    renderSectionData () {
      const type = this.layoutSetting.section.type
      /* start of table data */
      if (type === 'table') {
        this.original.tableData = JSON.parse(JSON.stringify(this.dataSource.tableData))
        this.current.tableData = JSON.parse(JSON.stringify(this.original.tableData))
      }
      /* end of table data //:~ */

      /* start of form data */
      if (type === 'form') {
        Object.assign(this.original.sectionData, this.dataSource.sectionData)
        Object.assign(this.current.sectionData, this.original.sectionData)
        if (this.assist.tool.isNotEmpty(this.dataSource.sectionData)) {
          const formKeyArr = []
          const sectionKeyArr = []
          for (const formField of this.layoutSetting.formFields) {
            if (formField.isEditable !== false) formKeyArr.push(formField.fieldKey)
            for (const key in this.dataSource.sectionData) {
              if (formField.fieldKey === key) {
                sectionKeyArr.push(key)
              }
            }
          }
          // find missing field key
          if (formKeyArr.length > sectionKeyArr.length) {
            let differArr = []
            differArr = formKeyArr.concat(sectionKeyArr).filter(function (v, i, arr) {
              return arr.indexOf(v) === arr.lastIndexOf(v)
            })
            for (const fieldKey of differArr) {
              this.current.sectionData[fieldKey] = null
            }
          }
        } else {
          for (const item of this.layoutSetting.formFields) {
            if (item.isEditable !== false) this.current.sectionData[item.fieldKey] = null
          }
        }

        const payrollFrequency = this.current.sectionData.payrollFrequency
        this.changeSelect(DataType.JsonKeyConst.PAYROLL_FREQUENCY, payrollFrequency)
      }
      /* end of form data //:~ */
    },
    transformJsonToArray (json, array, keyName, updateItemCallback, createItemCallback, formatValueCallback) {
      const keys = Object.keys(json)
      for (const key of keys) {
        let value = json[key]
        let id
        if (json[key].id) {
          id = json[key].id
        }
        if (formatValueCallback) {
          value = formatValueCallback(value)
        }
        let isExisted = false
        for (const index in array) {
          if (index >= 0) {
            const itemKey = array[index][keyName].toString()

            if (itemKey === key) {
              isExisted = true
              updateItemCallback(array[index], value, id)
              break
            }
          }
        }

        if (!isExisted) {
          const item = createItemCallback(key, value)
          array.push(item)
        }
      }
    },
    transformToAnswerData (questionAnswerData, answerData) {
      const $this = this

      this.transformJsonToArray(
        questionAnswerData,
        answerData,
        'question',
        function (item, value) {
          item.answer = value.value
          item.appendix = value.appendix
        },
        function (key, value) {
          return {
            loanId: $this.loanId,
            question: parseInt(key, 10),
            answer: value.value,
            appendix: value.appendix
          }
        }
      )
    },
    saveAction () {
      this.flag = false
      let flag = true
      const observer = this.$refs.observer
      if (TOOL.isNull(observer)) {
        this.saveOperation()
      } else {
        observer.map((item, index) => {
          item.validate().then(result => {
            if (!result) {
              flag = false
            }
          }).then(() => {
            if (flag && !(this.flag)) {
              if (this.isEditProfile()) {
                this.$validator.validateAll('form').then(res => {
                  if (res) {
                    this.saveOperation()
                  } else {
                    window._VMA.$emit('SHOW_SNACKBAR', {
                      text: 'Please select Authorization Type !',
                      color: 'warning'
                    })
                  }
                })
              } else {
                this.saveOperation()
              }
              this.flag = true
            }
          })
        })
      }
    },
    listLoanErrorData () {
      if (TOOL.isNotEmpty(this.loanId)) {
        const $this = this
        const request = {
          loanId: this.loanId,
          feature: 'BLACK_LIST'
        }
        OriginationApi.listSectionFeature(request, function (callbackData) {
          $this.$emit('updatePersonalNameInBlackList', false)
          $this.$emit('updateEmploymentNameInBlackList', false)
          if (TOOL.isNotEmpty(callbackData)) {
            for (const item of callbackData) {
              const fieldKey = item.fieldKey
              if (fieldKey === DataType.BlacklistEnum.PERSONAL_NAME) {
                $this.$emit('updatePersonalNameInBlackList', true)
              }
              if (fieldKey === DataType.BlacklistEnum.EMPLOYMENT_NAME) {
                $this.$emit('updateEmploymentNameInBlackList', true)
              }
            }
          }
          $this.setting.display = false
        })
      }
    },
    async saveOperation () {
      this.dialog.loading = true
      const $this = this
      if ((TOOL.isNotEmpty(this.current.sectionData.homePhone) && !await this.isValidatePhone(this.current.sectionData.homePhone)) ||
        (TOOL.isNotEmpty(this.current.sectionData.cellPhone) && !await this.isValidatePhone(this.current.sectionData.cellPhone)) ||
        (TOOL.isNotEmpty(this.current.sectionData.alternativePhone) && !await this.isValidatePhone(this.current.sectionData.alternativePhone)) ||
        (TOOL.isNotEmpty(this.current.sectionData.employerPhone) && !await this.isValidatePhone(this.current.sectionData.employerPhone)) ||
        (TOOL.isNotEmpty(this.current.sectionData.bankPhone) && !await this.isValidatePhone(this.current.sectionData.bankPhone))) {
        popup.popupForWarning(
          'Do you want to ignore the wrong format of the phone number and continue to save?',
          { showCancelButton: true },
          function () {
            $this.checkBankCard()
          })
        this.dialog.loading = false
        return
      }
      $this.checkBankCard()
    },
    checkBankCard () {
      if (this.layoutSetting.section.name === 'payment-option') {
        const $this = this
        let paymentMode = null
        const portfolio = this.store.getters.getPortfolioJson
        if (TOOL.isEmpty(portfolio.individualTransactionMode) || portfolio.individualTransactionMode === '0') {
          paymentMode = this.current.sectionData.paymentMode
        } else {
          paymentMode = this.current.sectionData.defaultDisbursementMode
        }
        if (TOOL.isNotEmpty(paymentMode) && paymentMode === DataType.TransactionModeEnum.INSTANT_FUND.value) {
          let checkBankCardLoanId = this.loanId
          if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
            checkBankCardLoanId = this.slaveLoanId
          }
          InstantApi.getAvailableCard(this.loan.id, function (result) {
            ProcessingApi.checkLoanFlag(checkBankCardLoanId, function () {
              $this.signStatusCheck()
            }, function () {
              popup.popupForWarning(
                'Repayment Mode changed to ACH due to previous rejected payments, are you sure to change repayment mode back to ACH?',
                {
                  showCancelButton: true,
                  confirmButtonText: 'Yes',
                  cancelButtonText: 'No'
                },
                function () {
                  ProcessingApi.updateFlag(checkBankCardLoanId)
                })
            })
          }, function (failureResult) {
            popup.popupForWarning('This loan is not bound to the card, you should bind the debit card first!')
            $this.dialog.loading = false
          })
        } else {
          this.signStatusCheck()
        }
      } else {
        this.signStatusCheck()
      }
    },
    signStatusCheck () {
      const $this = this
      let isAffectLoanAgreement = false
      if (this.layoutSetting.section.name === 'bank' && TOOL.isNotEmpty($this.current.sectionData.numberOfOpenLoans)) {
        $this.current.sectionData.numberOfOpenLoans = Number($this.current.sectionData.numberOfOpenLoans)
      }
      for (const item of this.layoutSetting.formFields) {
        if (TOOL.isNotEmpty(item.mask) && this.assist.tool.isNotEmpty(this.current.sectionData[item.fieldKey])) {
          this.current.sectionData[item.fieldKey] = this.current.sectionData[item.fieldKey].replace(/[^0-9]/ig, '')
          this.original.sectionData[item.fieldKey] = ('' + this.original.sectionData[item.fieldKey]).replace(/[^0-9]/ig, '')
        }
      }
      const updatedKeys = $this.compareData($this.original, $this.current)
      const affectedField = updatedKeys.filter(
        item => LOAN_AGREEMENT_FACTOR.includes(item)
      )
      if (affectedField.length > 0) {
        isAffectLoanAgreement = true
      }

      if (this.loan.loanStatus === DataType.LoanStatus.AGENT_REVIEW.value || this.loan.loanStatus === DataType.LoanStatus.CORRECTOR_REVIEW.value) {
        OriginationApi.getLoanSignData($this.loanId, function (result) {
          const signStatus = $this.assist.tool.isNotEmpty(result) ? result.signStatus : null
          if ((signStatus === DataType.SignStatusEnum.SIGNED.value) && isAffectLoanAgreement) {
            popup.popupForInfo('This change will void the previous loan agreement. Are you sure continue?', {
              showCancelButton: true,
              confirmButtonText: 'Yes',
              cancelButtonText: 'No'
            }, function () {
              OriginationBusinessApi.voidLoanAgreement({ loanId: $this.loanId }, function (result) {
                $this.clickSaveEventHandler()
              }, function (result) {
                popup.popupForWarning(result.message)
              })
            }, function () {
              $this.dialog.loading = false
            })
          } else if ($this.assist.tool.isNotEmpty(signStatus) && (signStatus === DataType.SignStatusEnum.NOT_SIGNED.value) && isAffectLoanAgreement) {
            OriginationBusinessApi.suspendLoanAgreement({ loanId: $this.loanId }, function () {
              $this.clickSaveEventHandler()
            }, function (result) {
              popup.popupForWarning(result.message, null, function () {
                $this.dialog.loading = false
              })
            })
          } else if ($this.assist.tool.isNotEmpty(signStatus) && (signStatus === DataType.SignStatusEnum.IN_PROGRESS.value) && isAffectLoanAgreement) {
            popup.popupForWarning('Customer is signing the document, please try again later')
          } else if ($this.assist.tool.isNotEmpty(signStatus) && (signStatus === DataType.SignStatusEnum.RECOVER_IN_PROGRESS.value) && isAffectLoanAgreement) {
            popup.popupForWarning('Document recovery is in progress, please try again later')
          } else {
            $this.clickSaveEventHandler()
          }
        })
      } else {
        $this.clickSaveEventHandler()
      }
    },
    async clickSaveEventHandler () {
      try {
        const $this = this
        const type = this.layoutSetting.section.type
        const action = this.setting.action
        const feature = this.layoutSetting.section.name
        const questionGroup = this.layoutSetting.questionGroup
        const tableData = this.current.tableData

        const newSectionData = {}
        let newTableData = []
        let newAnswerData = []
        let blackListReturn = []
        let warningListReturn = []

        const updateSectionData = {
          loanId: $this.loanId,
          sectionKey: TOOL.getSectionKeyByFeature(feature)
        }

        /* start of edit */
        if (action === 'EDIT') {
          /* start of question */
          if (questionGroup) {
            this.transformToAnswerData(this.current.questionAnswerData, this.current.answerData)
            const questionList = []
            for (const question of this.current.answerData) {
              if (question.answer !== false) {
                questionList.push(question)
              }
            }
            await OriginationApi.batchUpdateQuestionData(questionList, function (result) {
              OriginationApi.listLoanQuestionData($this.loanId, function (result) {
                newAnswerData = result
              })
            })
          }
          /* end of question //:~ */

          /* start of form */
          if (type === 'form') {
            Object.assign(newSectionData, this.current.sectionData)

            switch (feature) {
              case 'personal': {
                if (this.assist.tool.isEmpty(newSectionData.military)) {
                  newSectionData.military = 0
                }
                break
              }
              case 'payment-option': {
                // TODO get programId from maintenance
                if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value && feature === 'payment-option') {
                  newSectionData.program = 1019
                }
                if (TOOL.isNotEmpty(this.dataSource.programArray)) {
                  for (const program of this.dataSource.programArray) {
                    if (program.id === newSectionData.program) {
                      newSectionData.interestRate = program.interestRate
                      break
                    }
                  }
                }
                break
              }
              default:
                break
            }

            await Object.assign(newSectionData, { loanId: this.loanId })
            if (this.loan.phase !== DataType.LoanPhaseEnum.LOAN_ORIGINATION.value) {
              await Object.assign(newSectionData, {
                authorizationType: this.dialog.authorizationType,
                note: this.dialog.note
              })
            }

            if (feature === 'payment-option') {
              const paymentOptionSaveData = {}
              if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
                paymentOptionSaveData.slaveLoanId = this.slaveLoanId
              }
              paymentOptionSaveData.effectiveDate = newSectionData.effectiveDate
              paymentOptionSaveData.program = newSectionData.program
              paymentOptionSaveData.loanId = this.loanId
              paymentOptionSaveData.interestRate = newSectionData.interestRate
              updateSectionData.slaveLoanId = this.slaveLoanId
              updateSectionData.featureData = paymentOptionSaveData
              const portfolio = this.store.getters.getPortfolioJson
              if (TOOL.isEmpty(portfolio.individualTransactionMode) || portfolio.individualTransactionMode === '0') {
                paymentOptionSaveData.defaultDisbursementMode = newSectionData.paymentMode
                paymentOptionSaveData.defaultRepaymentMode = newSectionData.paymentMode
                newSectionData.defaultDisbursementMode = newSectionData.paymentMode
                newSectionData.defaultRepaymentMode = newSectionData.paymentMode
              } else {
                paymentOptionSaveData.defaultDisbursementMode = newSectionData.defaultDisbursementMode
                paymentOptionSaveData.defaultRepaymentMode = newSectionData.defaultRepaymentMode
              }
            } else {
              updateSectionData.featureData = newSectionData
            }
          }
          /* end of form //:~ */

          /* start of table */
          if (type === 'table') {
            let hasWarning = false
            const tableDataArr = []

            for (const item of tableData) {
              if ('amount' in item && 'loanId' in item && 'balance' in item && 'payrollDate' in item &&
                'payrollType' in item && 'vob' in item) {
                tableDataArr.push(item)
              } else {
                hasWarning = true
              }
            }

            if (hasWarning) {
              popup.popupForWarning('fill it up')
              this.dialog.loading = false
              return
            }

            this.current.tableData = tableDataArr
            updateSectionData.featureData = tableDataArr
          }
          /* end of table //:~ */
          /* end of edit //:~ */
        } else if (action === 'AUDIT') {
          /* start of audit */
          if (type === 'form') {
            Object.assign(newSectionData, this.current.sectionData)
            await Object.assign(newSectionData, { loanId: this.loanId })
          }
          updateSectionData.isAudit = true
        }
        /* end of audit //:~ */

        if (this.loan.phase === DataType.LoanPhaseEnum.LOAN_ORIGINATION.value) {
          await OriginationApi.updateSection(updateSectionData, function (result) {
            if (TOOL.isNotEmpty(result)) {
              if (feature === SystemEnum.SECTION_FEATURE.BANK_RECORDS) {
                newTableData = result.current
              }
              if (feature === SystemEnum.SECTION_FEATURE.LOAN_DETAILS) {
                $this.$eventBus.$emit('judgeSameDayEvent')
                if (DataType.TransactionModeEnum.INSTANT_FUND.value === updateSectionData.featureData.defaultDisbursementMode) {
                  $this.$eventBus.$emit('judgeCreditCardEvent', true)
                } else {
                  $this.$eventBus.$emit('judgeCreditCardEvent', false)
                }
              }
              if (feature === SystemEnum.SECTION_FEATURE.PERSONAL || feature === SystemEnum.SECTION_FEATURE.BANK) {
                if (TOOL.isNotEmpty(result.blackList)) {
                  blackListReturn = result.blackList
                  for (const item of blackListReturn) {
                    const filedKey = $this.assist.tool.transferBlackFieldKey(item)
                    if (filedKey === DataType.BlacklistEnum.EMAIL) {
                      $this.blackList.email = true
                    }
                    if (filedKey === DataType.BlacklistEnum.SSN) {
                      $this.blackList.ssn = true
                    }
                    if (filedKey === DataType.BlacklistEnum.STATE) {
                      $this.blackList.state = true
                    }
                    if (filedKey === DataType.BlacklistEnum.ACCOUNT_NUMBER) {
                      $this.blackList.accountNo = true
                    }
                    if (filedKey === DataType.BlacklistEnum.ROUTING_NUMBER) {
                      $this.blackList.routingNo = true
                    }
                    if (filedKey === DataType.BlacklistEnum.ZIP) {
                      $this.blackList.zip = true
                    }
                    if (filedKey === DataType.BlacklistEnum.BIRTHDAY) {
                      $this.blackList.birthday = true
                    }
                  }
                }
                $this.$eventBus.$emit('showBlackListIcon', {
                  section: $this.layoutSetting.section.name,
                  data: $this.blackList
                })
                // 修改后源数据恢复
                $this.blackList = {
                  ssn: false,
                  email: false,
                  state: false,
                  accountNo: false,
                  routingNo: false,
                  zip: false,
                  birthday: false
                }
              }
            }
            if (feature === SystemEnum.SECTION_FEATURE.PERSONAL || feature === SystemEnum.SECTION_FEATURE.EMPLOYMENT || feature === SystemEnum.SECTION_FEATURE.BANK_RECORDS) {
              // warningList
              if (TOOL.isNotEmpty(result.warning)) {
                warningListReturn = result.warning
                for (const item of warningListReturn) {
                  const filedKey = $this.assist.tool.transferBlackFieldKey(item)
                  if (filedKey === DataType.WarningListEnum.ZIP) {
                    $this.warningList.zip = true
                  } else if (filedKey === DataType.WarningListEnum.EMAIL) {
                    $this.warningList.email = true
                  } else if (filedKey === DataType.WarningListEnum.LAST_PAY_DAY) {
                    $this.warningList.lastPayday = true
                  }
                }
              } else if (feature === SystemEnum.SECTION_FEATURE.BANK_RECORDS) {
                $this.warningList.lastPayday = false
              }
              $this.$eventBus.$emit('showWarningListIcon', {
                section: $this.layoutSetting.section.name,
                data: $this.warningList
              })

              // 修改后源数据恢复
              $this.warningList = {
                zip: false,
                email: false,
                lastPayday: false
              }
            }
            if ($this.assist.tool.isNotEmpty(newSectionData)) $this.current.sectionData = newSectionData
            if ($this.assist.tool.isNotEmpty(newTableData)) $this.current.tableData = newTableData
            if ($this.assist.tool.isNotEmpty(newAnswerData)) $this.current.answerData = newAnswerData
            $this.$eventBus.$emit('sectionUpdated', {
              current: JSON.parse(JSON.stringify($this.current)),
              action: $this.setting.action,
              feature: $this.layoutSetting.section.name
            })
          }, function (result) {
            popup.popupForWarning(result.message)
          })
        } else {
          if (type === 'table') {
            const requestData = { loanId: this.loanId, bankRecords: this.current.tableData }
            await OriginationApi.updateLoanBankRecordsData(requestData, function (result) {
              if (TOOL.isNotEmpty(result) && TOOL.isNotEmpty(result.statementArray)) {
                newTableData = result.statementArray
              } else {
                newTableData = []
              }
            }, function () {
            })
          } else {
            let sectionData = {}
            if (feature === 'payment-option') {
              sectionData = {
                loanId: newSectionData.loanId,
                effectiveDate: newSectionData.effectiveDate,
                program: newSectionData.program,
                interestRate: newSectionData.interestRate,
                paymentMode: newSectionData.paymentMode
              }
              if (this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value) {
                sectionData.slaveLoanId = this.slaveLoanId
              }
            } else {
              sectionData = newSectionData
            }
            await OriginationApi.updateLoanFeatureData(feature, sectionData, function (result) {
              if ($this.assist.tool.isNotEmpty(newSectionData)) $this.current.sectionData = newSectionData
              if ($this.assist.tool.isNotEmpty(newTableData)) $this.current.tableData = newTableData
              if ($this.assist.tool.isNotEmpty(newAnswerData)) $this.current.answerData = newAnswerData
              $this.$eventBus.$emit('sectionUpdated', {
                current: JSON.parse(JSON.stringify($this.current)),
                action: $this.setting.action,
                feature: $this.layoutSetting.section.name
              })
            }, function (err) {
              popup.popupForError(err.message)
            })
          }
        }
        /* start of emit event */
        if ($this.layoutSetting.section.name === 'personal' && $this.isEffectCustomerLocalTime($this.original, $this.current)) {
          $this.$eventBus.$emit('setPersonalData', newSectionData)
        }
        /* end of emit event //:~ */
        $this.dialog.loading = false
        // this.setting.display = false
        $this.listLoanErrorData()
      } catch (error) {
        console.log('>>> ERROR <<<')
        console.log(error)
      }
    },
    isEffectCustomerLocalTime (originalData, currentData) {
      let result = false
      const customerLocalTimeArray = ['state', 'homePhone', 'cellPhone']
      const currentSectionData = currentData.sectionData
      const originalSectionData = originalData.sectionData

      for (const item of customerLocalTimeArray) {
        if (TOOL.isNotEmpty(currentSectionData[item]) && currentSectionData[item] !== originalSectionData[item]) {
          result = true
          break
        }
      }

      return result
    },
    compareData (originalData, currentData) {
      const updatedKeys = []
      // compare sectionData
      const currentSectionData = currentData.sectionData
      const originalSectionData = originalData.sectionData
      const sectionName = this.layoutSetting.section.name
      const fieldKeys = Object.keys(currentSectionData)

      // compare form
      for (const key of fieldKeys) {
        if (TOOL.isNotEmpty(currentSectionData[key]) && currentSectionData[key] !== originalSectionData[key]) {
          updatedKeys.push(key)
        }
      }

      // compare question
      if (TOOL.isNotEmpty(currentData.questionAnswerData)) {
        let answer
        const questionAnswerData = currentData.questionAnswerData[5].value
        for (const item of currentData.answerData) {
          if (item.question === 5) {
            answer = item.answer
          }
        }
        if (answer !== questionAnswerData) {
          updatedKeys.push('paydayOnAvailable')
        }
      }

      // compare table
      if (sectionName === 'statement') {
        const currentTable = currentData.tableData
        const originalTable = originalData.tableData
        if (Crypto.md5(JSON.stringify(currentTable)) !== Crypto.md5(JSON.stringify(originalTable))) {
          updatedKeys.push('statement')
        }
      }

      if (this.layoutSetting.section.name === 'payment-option' && this.loan.loanType === DataType.LoanTypeEnum.MULTIPLE_ADVANCE_LOAN.value && TOOL.isNotEmpty(updatedKeys)) {
        updatedKeys.push('program')
      }
      return updatedKeys
    },
    setFieldDisableStatus (field) {
      return field.isEditable === false
    },
    setTableDisableStatus (data) {
      if (this.setting.action === 'EDIT') {
        this.isTableDisabled = data
      } else {
        return true
      }
    },
    changeRadio (questionId) {
      if (questionId === 2 && this.current.questionAnswerData[questionId].value === DataType.AnswerEnum.NO.value) {
        this.current.questionAnswerData[questionId].appendix = ''
      }
      this.$forceUpdate()
    },
    editItems (action, item, index) {
      const $this = this
      const tableData = $this.current.tableData
      const lastOne = $this.current.tableData.length - 1

      switch (action) {
        case 'ADD':
          if ($this.assist.tool.isNotEmpty(tableData)) {
            if ('id' in tableData[lastOne] && Object.keys(tableData[lastOne]).length === 7) {
              tableData.push({ loanId: $this.loanId })
            } else if (!('id' in tableData[lastOne]) && Object.keys(tableData[lastOne]).length === 6) {
              tableData.push({ loanId: $this.loanId })
            } else {
              popup.popupForWarning('fill it up')
            }
          } else {
            tableData.push({ loanId: $this.loanId })
          }
          $this.dialog.tableDatePicker.push(false)
          break
        case 'DELETE':
          if (tableData[index] === item) {
            // remove from table
            tableData.splice(index, 1)
          }
          break
        default:
          break
      }
    },
    async isValidatePhone (phone) {
      let isValidate = false
      const input = {
        num: phone,
        regionCode: 'US'
      }
      await AlgorithmApi.isValidatePhone(input, function (res) {
        isValidate = res
      })
      return isValidate
    },
    hideProfileSectionDialog () {
      this.setting.display = false
    }
  }
}
</script>

<style scoped>
.header {
  position: relative;
  z-index: 999;
  top: 0;
  background-color: white;
}

.content {
  max-height: 600px;
  overflow: auto;
}

.card-text-layout {
  margin: -5px 0px;
  padding: -30px -20px;
}

.footer {
  position: relative;
  z-index: 999;
  bottom: 0;
  background-color: white;
}

.field-audit {
  font-size: 22px;
  padding-right: 12px;
  padding-top: 15px;
}

.section-banner {
  margin: 10px 0;
  font-size: 25px;
}

span {
  min-width: 110px;
}

.blue--text.text--darken-1 {
  color: #00181b !important;
}

.v-input--radio-group--column .v-input--radio-group__input {
  -webkit-box-orient: horizontal;
  -webkit-box-direction: unset;
  flex-direction: row;
}

.profile-section-dialog .theme--dark.v-btn.v-btn--disabled {
  color: rgba(0, 0, 0, 0.42) !important;
}

.profile-section-dialog .theme--dark.v-btn.v-btn--disabled .v-icon {
  color: rgba(0, 0, 0, 0.42) !important;
}
</style>
